import React, { useEffect, useState } from 'react';
import { useTypedTranslation } from '@app/hooks';
import {PasslyCheckbox, PasslyMaskedInput, PasslyTextField} from '@app/components';
import { translationVariable, digitRegex } from '@app/helpers';
import { TranslationsTypes } from '@app/models';
import { RadioButton } from '@itglue/kaseya-ui';

interface IProps {
    checkValidation: Function;
    patchData: IData;
}

interface IExpiry {
    days: number;
    hours: number;
}

interface IData {
    Enabled: boolean;
    Type: TrustTypes;
    Period: number;
}

enum ExpiryTypes {
    days = 'days',
    hours = 'hours'
}

enum TrustTypes {
    rolling = 'rolling',
    explicit = 'explicit'
}

type Convert = (type: ExpiryTypes, period: number) => number | undefined;

export const TrustDevices = (props: IProps) => {
    const { checkValidation, patchData } = props;
    const { translation } = useTypedTranslation();

    const convertConfig: Convert = (type, period) => {
        if (!!period) {
            switch (type) {
                case ExpiryTypes.days:
                    return Math.floor(period) || 0;
                case ExpiryTypes.hours:
                    return Math.round((period % 1) * 24) || 0;
            }
        }
        return;
    };

    const [trustDevice, setTrustDevice] = useState<boolean>(patchData?.Enabled ?? true);
    const [selectedTrustMode, setSelectedTrustMode] = useState<TrustTypes>(patchData?.Type ?? TrustTypes.rolling);
    const [expiry, setExpiry] = useState<IExpiry>({
        days: convertConfig(ExpiryTypes.days, patchData?.Period) ?? 30,
        hours: convertConfig(ExpiryTypes.hours, patchData?.Period) ?? 0
    });

    const handleChange = (value: string) => {
        setSelectedTrustMode(value as TrustTypes);
    };

    const handleExpiration = (type: ExpiryTypes, event: React.ChangeEvent<{ value: unknown }>) => {
        let value: number = event.target.value as number;
        value = !value ? 0 : value;

        switch (type) {
            case ExpiryTypes.days:
                setExpiry({ ...expiry, days: value });
                break;

            case ExpiryTypes.hours:
                setExpiry({ ...expiry, hours: value >= 24 ? 23 : value });
                break;

            default:
                break;
        }
    };

    const getExplicitLabel = (expiry: IExpiry, labelTranslation: TranslationsTypes): string => {
        const expiration: string =
            expiry.days + ' ' + translation('DEVICE_TRUST_PERIOD_UNIT') + ' ' + expiry.hours + ' ' + translation('HOURS_LOWER');
        return translation(labelTranslation).replace(translationVariable, expiration);
    };

    const periodValue = (expiry: IExpiry): number => {
        return expiry.days / 1 + expiry.hours / 24;
    };

    useEffect(() => {
        if (expiry.days.toString() === '0' && expiry.hours.toString() === '0') {
            checkValidation(false, null);
        } else {
            const data: IData = {
                Enabled: trustDevice,
                Type: selectedTrustMode,
                Period: periodValue(expiry)
            };

            checkValidation(true, data);
        }
    }, [trustDevice, selectedTrustMode, expiry]);

    const changeisTrustDevice = () => setTrustDevice(!trustDevice);

    return (
        <div className='trustDevice'>
            <PasslyCheckbox
                name='enableDeviceTrust'
                checked={trustDevice}
                onChange={changeisTrustDevice}
                label={translation('ENABLE_DEVICE_TRUST')}
            />

            {trustDevice && (
                <>
                    <h4 className='trustDevice__header'>{translation('DEVICE_TRUST_MODE')}</h4>
                    <RadioButton
                        value={TrustTypes.rolling}
                        label={getExplicitLabel(expiry, 'DEVICE_TRUST_ROLLING_EXPIRY')}
                        name='trust-type'
                        checked={selectedTrustMode === TrustTypes.rolling}
                        onChange={handleChange}
                    />

                    {selectedTrustMode === TrustTypes.rolling && (
                        <div className='trustDevice__form'>
                            <div className='input__container'>
                                <PasslyTextField
                                    label=''
                                    value={expiry.days}
                                    onChange={(event: any) => handleExpiration(ExpiryTypes.days, event)}
                                    id='rolling-days'
                                    InputProps={{
                                        inputComponent: PasslyMaskedInput,
                                        inputProps: {
                                            maskSize: 8,
                                            maskRegex: digitRegex
                                        },
                                    }} />
                                <span className='input__text'>{translation('DEVICE_TRUST_PERIOD_UNIT')}</span>
                            </div>
                            <div className='input__container'>
                                <PasslyTextField
                                    label=''
                                    value={expiry.hours}
                                    onChange={(event: any) => handleExpiration(ExpiryTypes.hours, event)}
                                    id='rolling-hours'
                                    InputProps={{
                                        inputComponent: PasslyMaskedInput,
                                        inputProps: {
                                            maskSize: 2,
                                            maskRegex: digitRegex
                                        },
                                    }} />
                                <span className='input__text'>{translation('HOURS_LOWER')}</span>
                            </div>
                        </div>
                    )}
                    <RadioButton
                        value={TrustTypes.explicit}
                        label={getExplicitLabel(expiry, 'DEVICE_TRUST_EXPLICIT_EXPIRY')}
                        name='trust-type'
                        checked={selectedTrustMode === TrustTypes.explicit}
                        onChange={handleChange}
                    />

                    {selectedTrustMode === TrustTypes.explicit && (
                        <div className='trustDevice__form'>
                            <div className='input__container'>
                                <PasslyTextField
                                    label=''
                                    value={expiry.days}
                                    onChange={(event: any) => handleExpiration(ExpiryTypes.days, event)}
                                    id='explicit-days'
                                    InputProps={{
                                        inputComponent: PasslyMaskedInput,
                                        inputProps: {
                                            maskSize: 8,
                                            maskRegex: digitRegex
                                        },
                                    }} />
                                <span className='input__text'>{translation('DEVICE_TRUST_PERIOD_UNIT')}</span>
                            </div>
                            <div className='input__container'>
                                <PasslyTextField
                                    label=''
                                    value={expiry.hours}
                                    onChange={(event: any) => handleExpiration(ExpiryTypes.hours, event)}
                                    id='explicit-hours'
                                    InputProps={{
                                        inputComponent: PasslyMaskedInput,
                                        inputProps: {
                                            maskSize: 2,
                                            maskRegex: digitRegex
                                        },
                                    }} />
                                <span className='input__text'>{translation('HOURS_LOWER')}</span>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
